/**
 * LayoutSection - Visual layout preview cards for page customization
 * Design: Notion-style visual preview thumbnails
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const LAYOUT_CATEGORIES = [
    {
        id: 'homepage',
        title: 'Homepage Layout',
        options: [
            { id: 'default', name: 'Default', wireframe: 'grid' },
            { id: 'topic-focus', name: 'Topic Focus', wireframe: 'topics' },
            { id: 'author-spotlight', name: 'Author Spotlight', wireframe: 'author' },
            { id: 'long-form', name: 'Long Form', wireframe: 'long' },
        ],
    },
    {
        id: 'categoryPage',
        title: 'Category Page Layout',
        options: [
            { id: 'default', name: 'Default', wireframe: 'grid' },
            { id: 'topic-grid', name: 'Topic Grid', wireframe: 'topics' },
            { id: 'timeline', name: 'Timeline', wireframe: 'timeline' },
        ],
    },
    {
        id: 'articleDetail',
        title: 'Article Detail Layout',
        options: [
            { id: 'default', name: 'Default', wireframe: 'article' },
            { id: 'immersive', name: 'Immersive', wireframe: 'immersive' },
            { id: 'analytical', name: 'Analytical', wireframe: 'analytic' },
            { id: 'collaborative', name: 'Collaborative', wireframe: 'collab' },
        ],
    },
];

/**
 * Render a mini wireframe preview based on layout type
 */
function LayoutWireframe({ type }) {
    switch (type) {
        case 'grid':
            return (
                <div className="layout-wireframe">
                    <div className="layout-wireframe-row">
                        <div className="layout-wireframe-block"></div>
                        <div className="layout-wireframe-block"></div>
                        <div className="layout-wireframe-block"></div>
                    </div>
                    <div className="layout-wireframe-row">
                        <div className="layout-wireframe-block"></div>
                        <div className="layout-wireframe-block"></div>
                        <div className="layout-wireframe-block"></div>
                    </div>
                </div>
            );
        case 'topics':
            return (
                <div className="layout-wireframe">
                    <div className="layout-wireframe-row" style={{ flex: 0.5 }}>
                        <div className="layout-wireframe-block"></div>
                    </div>
                    <div className="layout-wireframe-row">
                        <div className="layout-wireframe-block"></div>
                        <div className="layout-wireframe-block"></div>
                    </div>
                    <div className="layout-wireframe-row">
                        <div className="layout-wireframe-block"></div>
                        <div className="layout-wireframe-block"></div>
                    </div>
                </div>
            );
        case 'author':
            return (
                <div className="layout-wireframe">
                    <div className="layout-wireframe-row" style={{ flex: 0.4 }}>
                        <div className="layout-wireframe-block" style={{ borderRadius: '50%', flex: 'none', width: '40%' }}></div>
                    </div>
                    <div className="layout-wireframe-row">
                        <div className="layout-wireframe-block"></div>
                        <div className="layout-wireframe-block"></div>
                    </div>
                </div>
            );
        case 'long':
            return (
                <div className="layout-wireframe">
                    <div className="layout-wireframe-row" style={{ flex: 0.6 }}>
                        <div className="layout-wireframe-block hero"></div>
                    </div>
                    <div className="layout-wireframe-row" style={{ flex: 0.3 }}>
                        <div className="layout-wireframe-block"></div>
                    </div>
                </div>
            );
        case 'timeline':
            return (
                <div className="layout-wireframe" style={{ flexDirection: 'row' }}>
                    <div style={{ width: '4px', background: 'var(--theme-primary, #3b82f6)', borderRadius: '2px', margin: '0 8px' }}></div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div className="layout-wireframe-block" style={{ height: '25%' }}></div>
                        <div className="layout-wireframe-block" style={{ height: '25%' }}></div>
                        <div className="layout-wireframe-block" style={{ height: '25%' }}></div>
                    </div>
                </div>
            );
        case 'article':
            return (
                <div className="layout-wireframe">
                    <div className="layout-wireframe-row" style={{ flex: 0.3 }}>
                        <div className="layout-wireframe-block"></div>
                    </div>
                    <div className="layout-wireframe-row">
                        <div className="layout-wireframe-block" style={{ flex: 2 }}></div>
                        <div className="layout-wireframe-block sidebar"></div>
                    </div>
                </div>
            );
        case 'immersive':
            return (
                <div className="layout-wireframe">
                    <div className="layout-wireframe-row" style={{ flex: 0.5 }}>
                        <div className="layout-wireframe-block hero"></div>
                    </div>
                    <div className="layout-wireframe-row" style={{ flex: 0.4, maxWidth: '70%', margin: '0 auto' }}>
                        <div className="layout-wireframe-block"></div>
                    </div>
                </div>
            );
        case 'analytic':
            return (
                <div className="layout-wireframe" style={{ flexDirection: 'row' }}>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div className="layout-wireframe-block" style={{ height: '60%' }}></div>
                        <div className="layout-wireframe-block" style={{ height: '30%' }}></div>
                    </div>
                    <div style={{ flex: 0.4, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div className="layout-wireframe-block" style={{ height: '30%' }}></div>
                        <div className="layout-wireframe-block" style={{ height: '30%' }}></div>
                        <div className="layout-wireframe-block" style={{ height: '30%' }}></div>
                    </div>
                </div>
            );
        case 'collab':
            return (
                <div className="layout-wireframe" style={{ flexDirection: 'row' }}>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div className="layout-wireframe-block" style={{ height: '70%' }}></div>
                    </div>
                    <div style={{ flex: 0.5, display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '4px' }}>
                        <div className="layout-wireframe-block" style={{ height: '100%' }}></div>
                    </div>
                </div>
            );
        default:
            return (
                <div className="layout-wireframe">
                    <div className="layout-wireframe-row">
                        <div className="layout-wireframe-block"></div>
                    </div>
                </div>
            );
    }
}

export default function LayoutSection({ preferences, onPreferenceChange, disabled }) {
    const [layouts, setLayouts] = useState({
        homepage: preferences?.layouts?.homepage || 'default',
        categoryPage: preferences?.layouts?.categoryPage || 'default',
        articleDetail: preferences?.layouts?.articleDetail || 'default',
    });
    const [saving, setSaving] = useState(false);

    // Sync with props
    useEffect(() => {
        if (preferences?.layouts) {
            setLayouts({
                homepage: preferences.layouts.homepage || 'default',
                categoryPage: preferences.layouts.categoryPage || 'default',
                articleDetail: preferences.layouts.articleDetail || 'default',
            });
        }
    }, [preferences]);

    const handleLayoutChange = async (category, layoutId) => {
        const newLayouts = { ...layouts, [category]: layoutId };
        setLayouts(newLayouts);

        // Save to backend
        setSaving(true);
        try {
            await axios.patch('/q/api/user-preferences', {
                layouts: { [category]: layoutId }
            });
            onPreferenceChange?.({ type: 'success', text: 'Layout updated' });
        } catch (error) {
            onPreferenceChange?.({ type: 'error', text: 'Failed to save layout' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <section id="layouts" className="settings-section">
            <div className="settings-section-header">
                <span className="settings-section-icon">📐</span>
                <h2 className="settings-section-title">Page Layouts</h2>
            </div>
            <p className="settings-section-description">
                Choose how different pages are structured. Visual previews show the general layout pattern.
            </p>

            {LAYOUT_CATEGORIES.map((category) => (
                <div key={category.id} className="layout-category">
                    <h4 className="layout-category-title">{category.title}</h4>
                    <div className="layout-options" role="radiogroup" aria-label={category.title}>
                        {category.options.map((option) => (
                            <button
                                key={option.id}
                                className={`layout-card ${layouts[category.id] === option.id ? 'selected' : ''}`}
                                onClick={() => handleLayoutChange(category.id, option.id)}
                                disabled={disabled || saving}
                                role="radio"
                                aria-checked={layouts[category.id] === option.id}
                            >
                                <span className="layout-card-check">✓</span>
                                <div className="layout-card-preview">
                                    <LayoutWireframe type={option.wireframe} />
                                </div>
                                <div className="layout-card-name">{option.name}</div>
                            </button>
                        ))}
                    </div>
                </div>
            ))}
        </section>
    );
}
